import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "../i18n/useTranslations";
import { ArrowBack } from "../icons/ArrowBack";
import { ExpandLess } from "../icons/ExpandLess";
import { ExpandMore } from "../icons/ExpandMore";
import { useUser } from "../user/useUser";
import { labelToDashes, mapBooleanToOpenClose } from "../utils";
import { IMenuItem } from "./IMenu";
import { ListItemWithLinkOnly } from "./ListItemWithLinkOnly";
import { useMenu } from "./useMenu";

export enum MenuType {
  BACK_TITLE_ITEMS = "BACK_TITLE_ITEMS",
  ACCORDION = "ACCORDION"
}

type MenuItemProps = {
  className?: string;
  label?: IMenuItem["label"];
  children?: IMenuItem["children"];
  menuType: MenuType;
  shouldSkipAnimation: boolean;
  parentIsOpen: boolean;
  onClose: () => void;
  onClickOnAnyLink: () => void;
};

export function MenuItem({
  className,
  label,
  children,
  parentIsOpen,
  menuType,
  shouldSkipAnimation,
  onClose,
  onClickOnAnyLink
}: MenuItemProps) {
  const elRef = useRef<HTMLDivElement>();
  const { t } = useTranslations();
  const { user } = useUser();
  const [childIndexOpen, setChildIndexOpen] = useState(-1);

  const onChildToggle = (index: number) => (isOpen: boolean) => {
    setChildIndexOpen(isOpen ? index : -1);
    onClickOnAnyLink();
  };

  useEffect(() => {
    const el = elRef.current;
    el.classList.toggle("elm-invisible", !parentIsOpen);
    el.classList.toggle("elm-pointer-events-none", !parentIsOpen);
    el.style.height = parentIsOpen ? `auto` : "0px";
  }, [parentIsOpen, shouldSkipAnimation]);

  return (
    <div
      ref={elRef}
      className={`${className ?? ""} elm-pointer-events-none elm-invisible elm-overflow-hidden ${
        menuType === MenuType.BACK_TITLE_ITEMS
          ? "elm-px-4 elm-w-full elm-bg-white md:elm-pr-10 md:elm-pl-20"
          : ""
      }`}
    >
      {menuType === MenuType.BACK_TITLE_ITEMS && (
        <div className="elm-flex elm-flex-col elm-mt-2">
          <button
            tabIndex={0}
            data-track-label={`btn:back:from:${labelToDashes(label)}`}
            className="focus:elm-outline-none elm-pointer-events-auto elm--ml-2 elm-flex elm-justify-center elm-w-10 elm-cursor-pointer elm-bg-transparent elm-border-0 elm-px-0 elm-py-2"
            onClick={onClose}
            title={t("Back")}
            aria-label={t("Back")}
          >
            <ArrowBack />
          </button>
          <div
            role="heading"
            className="elm-text-dd-gray-900 elm-text-3xl elm-m-0 elm-ml-1 elm-pr-4 elm-mb-3 elm-font-semibold"
          >
            {label}
          </div>
        </div>
      )}
      <ul aria-label={label} className="elm-m-0 elm-p-0 elm-list-none">
        {children?.map((item, idx) =>
          item.children ? (
            <ListItemWithChildren
              shouldBeDisplayed={item.guard?.(user) ?? true}
              shouldSkipAnimation={false}
              key={idx}
              menuType={MenuType.ACCORDION}
              item={item}
              onChildToggle={onChildToggle(idx)}
              onClickOnAnyLink={onClickOnAnyLink}
              parentIsOpen={parentIsOpen && idx === childIndexOpen}
            />
          ) : (
            <ListItemWithLinkOnly
              shouldBeDisplayed={item.guard?.(user) ?? true}
              key={idx}
              item={item}
              className={
                menuType === MenuType.BACK_TITLE_ITEMS
                  ? "elm-border-0 elm-border-b elm-border-solid elm-border-gray-300"
                  : ""
              }
            />
          )
        )}
      </ul>
    </div>
  );
}

type ListItemWithChildrenProps = {
  item: IMenuItem;
  menuType: MenuType;
  parentIsOpen: boolean;
  shouldBeDisplayed: boolean;
  shouldSkipAnimation: boolean;
  onChildToggle?: (open: boolean) => void;
  onClickOnAnyLink: () => void;
  onChildOpen?: (isOpen: boolean) => void;
};

export function ListItemWithChildren({
  shouldBeDisplayed,
  shouldSkipAnimation,
  menuType,
  item,
  parentIsOpen,
  onChildToggle,
  onClickOnAnyLink,
  onChildOpen
}: ListItemWithChildrenProps) {
  const { isOpen: menuIsOpen } = useMenu();
  // controls the "accordion" mode
  const [isOpen, setOpen] = useState(false);
  const toggle = () => {
    setOpen(open => !open);
    onChildToggle?.(!isOpen);
    onClickOnAnyLink();
  };

  const isAccordion = menuType === MenuType.ACCORDION;
  const className = !isAccordion ? "elm-fixed elm-z-10 elm-pt-14 elm-top-0 elm-left-0" : "";

  useEffect(() => {
    if (!parentIsOpen) {
      // close me when the parent menu is closed
      setOpen(false);
    }
  }, [parentIsOpen]);

  useEffect(() => {
    if (!menuIsOpen) {
      // close me when the entire menu is closed
      setOpen(false);
    }
  }, [menuIsOpen]);

  useEffect(() => {
    onChildOpen?.(!isOpen);
  }, [isOpen, onChildOpen]);

  return (
    <li
      className={`elm-border-0 elm-border-b elm-border-solid elm-border-gray-300 ${
        shouldBeDisplayed ? "elm-block" : "elm-hidden"
      }`}
    >
      <button
        tabIndex={0}
        data-track-label={`btn:${mapBooleanToOpenClose(!isOpen)}:${labelToDashes(item.label)}`}
        className="focus:elm-outline-none elm-pl-6 elm-pr-5 elm-pointer-events-auto elm-cursor-pointer elm-font-sans elm-bg-transparent elm-text-lg elm-border-0 elm-px-0 elm-w-full elm-py-3.5 elm-flex elm-justify-start elm-text-dd-gray-900 elm-transition lg:hover:elm-text-dd-blue-800"
        onClick={toggle}
        title={item.label}
        aria-label={item.label}
      >
        <span
          className={`elm-flex-grow elm-text-left elm-pointer-events-none ${
            isOpen ? "elm-font-semibold" : ""
          }`}
        >
          {item.label}
        </span>
        {isAccordion ? isOpen ? <ExpandLess /> : <ExpandMore /> : null}
      </button>
      <MenuItem
        shouldSkipAnimation={shouldSkipAnimation}
        className={`${
          !isAccordion ? "elm-visible" : ""
        } elm-w-full elm-min-h-full elm-bottom-0 md:elm-max-w-md elm-overflow-y-auto elm-bg-white ${className}`}
        menuType={menuType ?? MenuType.ACCORDION}
        parentIsOpen={isOpen}
        onClose={toggle}
        onClickOnAnyLink={onClickOnAnyLink}
        label={item.label}
        children={item.children}
      />
    </li>
  );
}
